import gsap from 'gsap'
import Core from 'smooothy'
import SmoothScroll from '../utils/SmoothScroll'

export default class InfiniteSlider {
	constructor() {
		this.instances = []
		document.querySelectorAll('[data-infinite-slider]').forEach(wrapper => {
			this.instances.push(new InfiniteSliderInstance(wrapper))
		})
	}

	destroy() {
		this.instances.forEach(instance => instance?.destroy?.())
		this.instances = []
	}
}

class InfiniteSliderInstance extends Core {
	#isPaused = false
	#baseScrollSpeed = 0.05 // base auto-scroll (units per second)
	#velocityMultiplier = 0.05 // how strongly page scroll velocity boosts slider speed
	#maxVelocityBoost = 4 // cap the boost so fast flicks don't warp the slider
	#boostDecay = 16 // higher = boost decays faster when scrolling stops
	#currentBoost = 0 // smoothed boost value, signed (direction-aware)
	#wasDragging = false
	#lastTime = 0
	#handleLenisScroll = null

	constructor(wrapper, config = {}) {
		super(wrapper, {
			...config,
			infinite: true,
			snap: false,
		})

		this.abortController = new AbortController()
		this.resumeTimeout = null
		this.lenis = new SmoothScroll().lenis

		this.#handleLenisScroll = ({ velocity }) => {
			const boost = gsap.utils.clamp(
				-this.#maxVelocityBoost,
				this.#maxVelocityBoost,
				velocity * this.#velocityMultiplier
			)
			// Keep the strongest recent boost so it feels responsive, then decay in update().
			if (Math.abs(boost) > Math.abs(this.#currentBoost)) {
				this.#currentBoost = boost
			}
		}
		this.lenis?.on('scroll', this.#handleLenisScroll)

		// Override update to add continuous auto-scroll.
		// We track our own delta (capped) because smooothy's internal deltaTime
		// balloons after the slider is off-screen or the tab is backgrounded
		// (its update() early-returns while !isVisible, so lastTime stalls).
		const originalUpdate = super.update.bind(this)
		this.update = () => {
			const now = performance.now()
			const dt = this.#lastTime ? Math.min((now - this.#lastTime) / 1000, 1 / 30) : 0
			this.#lastTime = now

			// Ease the boost back toward zero each frame.
			this.#currentBoost *= Math.max(0, 1 - this.#boostDecay * dt)

			if (!this.#isPaused && this.isVisible && !this.isDragging) {
				const delta = (this.#baseScrollSpeed + this.#currentBoost) * dt
				this.target -= delta
				// Move current in lockstep so the Core's internal lerp doesn't add lag.
				this.current -= delta
			}

			originalUpdate()
			this.#checkDragging()
		}

		// Bind AFTER override so gsap.ticker drives the overridden version
		this.boundUpdate = this.update.bind(this)
		gsap.ticker.add(this.boundUpdate)

		this.#setupPauseOnInteraction()
		this.#handleLinks()
	}

	#checkDragging() {
		if (this.isDragging && !this.#wasDragging) {
			this.#isPaused = true
			this.#wasDragging = true
		} else if (!this.isDragging && this.#wasDragging) {
			this.#wasDragging = false
			this.#scheduleResume()
		}
	}

	#scheduleResume() {
		clearTimeout(this.resumeTimeout)
		this.resumeTimeout = setTimeout(() => {
			this.#isPaused = false
		}, 2000)
	}

	#setupPauseOnInteraction() {
		return

		const slider = this.wrapper
		const { signal } = this.abortController

		slider.addEventListener('mouseenter', () => (this.#isPaused = true), { signal })
		slider.addEventListener('mouseleave', () => (this.#isPaused = false), { signal })
		slider.addEventListener('touchstart', () => (this.#isPaused = true), { signal })
		slider.addEventListener('touchend', () => this.#scheduleResume(), { signal })
	}

	#handleLinks() {
		const { signal } = this.abortController

		let startX = 0
		let startY = 0
		let isDragging = false

		// Keep pointer events on the links so :hover (and the CSS group-hover
		// flick-up) still works. We only need to swallow the click that fires at
		// the end of a drag so the slider doesn't navigate away.
		const handleMouseDown = e => {
			startX = e.clientX
			startY = e.clientY
			isDragging = false
		}

		const handleMouseMove = e => {
			const deltaX = Math.abs(e.clientX - startX)
			const deltaY = Math.abs(e.clientY - startY)

			if (deltaX > 5 || deltaY > 5) {
				isDragging = true
			}
		}

		const handleClick = e => {
			if (isDragging) {
				e.preventDefault()
				e.stopPropagation()
			}
		}

		// Anchors are draggable by default; that native drag-and-drop steals the
		// pointer and breaks smooothy's drag. Disable it so the slider can drag.
		;[...this.wrapper.querySelectorAll('a')].forEach(item => {
			item.setAttribute('draggable', 'false')
		})
		this.wrapper.addEventListener('dragstart', e => e.preventDefault(), { signal })

		this.wrapper.addEventListener('mousedown', handleMouseDown, { signal })
		this.wrapper.addEventListener('mousemove', handleMouseMove, { signal })
		// Capture phase so we can cancel navigation before the link handles it.
		this.wrapper.addEventListener('click', handleClick, { signal, capture: true })
	}

	destroy() {
		this.abortController?.abort()
		clearTimeout(this.resumeTimeout)
		gsap.ticker.remove(this.boundUpdate)
		if (this.#handleLenisScroll) {
			this.lenis?.off('scroll', this.#handleLenisScroll)
		}
		super.destroy?.()
	}
}
